<template>
  <div>
    <SchemaTablePage
      ref="tablePageRef"
      :layout-mode="layoutMode"
      :immediate="false"
      :search-schema="searchSchema"
      :columns="tableColumns"
      :default-query="defaultQuery"
      :request="request"
      :build-params="buildParams"
      :parse-result="parseResult"
    >
      <template #table-header>
        <div class="actions">
          <el-button size="mini" type="primary" @click="handleAdd">新增</el-button>
        </div>
      </template>

      <template v-if="showAccountCell" #colMemberAccount="{ row }">
        <MemberAccountCell
          :row="row"
          :username="row.username"
          :uid="row.uid"
          :vip-level="row.vip_level"
          default-detail-tab-type="withdrawAccount"
        />
      </template>
      <template #colCardNo="{ row }">
        <div>{{ row.card_no || '-' }}</div>
      </template>
      <template #colCreatedAt="{ row }">{{ row.created_at | formatTimestamp }}</template>
      <template #colActions="{ row }">
        <el-button size="mini" type="text" @click="handleEdit(row)">编辑</el-button>
        <el-button size="mini" class="danger-button" type="text" @click="handleDelete(row.id)">删除</el-button>
      </template>
    </SchemaTablePage>
    <BankAccountFormDialog :visible.sync="formDialogVisible" :is-edit="isEdit" :form-data="formData" @success="handleFormSuccess" />
  </div>
</template>

<script>
import { deletePaymentMethod, getPaymentMethodList } from '@/api/member/account'
import { formatTimestamp } from '@/utils/time'
import BankAccountFormDialog from './ManageBankAccountFormDialog.vue'
import MemberAccountCell from '@/components/MemberAccountCell.vue'
import SchemaTablePage from '@/components/SchemaTablePage'

export default {
  name: 'ManageBankAccountManageTab',
  components: {
    BankAccountFormDialog,
    MemberAccountCell,
    SchemaTablePage,
  },
  filters: {
    formatTimestamp,
  },
  props: {
    memberInfo: {
      type: Object,
      default: () => ({}),
    },
    targetInfo: {
      type: Object,
      default: () => ({}),
    },
    isActive: {
      type: Boolean,
      default: true,
    },
    resetKey: {
      type: Number,
      default: 0,
    },
    showAccountCell: {
      type: Boolean,
      default: true,
    },
    layoutMode: {
      type: String,
      default: 'page',
    },
  },
  data() {
    return {
      defaultQuery: {
        username: '',
        card_no: '',
      },
      searchSchema: [
        { prop: 'username', label: '会员账号', component: 'input', componentProps: { placeholder: '请输入会员账号', clearable: true }},
        { prop: 'card_no', label: '提款账号', component: 'input', componentProps: { placeholder: '请输入提款账号', clearable: true }},
        { component: 'actions' },
      ],
      formDialogVisible: false,
      isEdit: false,
      formData: {
        id: '',
        card_type: '',
        pix_type: '',
        card_no: '',
        username: '',
      },
    }
  },
  computed: {
    tableColumns() {
      const cols = []
      if (this.showAccountCell) {
        cols.push({ prop: 'username', label: '会员账号', minWidth: 140, resizable: false, slot: 'colMemberAccount' })
      }
      cols.push(
        { prop: 'card_no', label: '提款账号', minWidth: 140, resizable: false, slot: 'colCardNo' },
        { prop: 'card_type', label: '银行名称', minWidth: 180, resizable: false },
        { prop: 'pix_type', label: '账号类型', minWidth: 100, resizable: false },
        { prop: 'created_at', label: '绑定时间', minWidth: 140, resizable: false, slot: 'colCreatedAt' },
        { width: 100, fixed: 'right', align: 'center', label: '操作', resizable: false, slot: 'colActions' },
      )
      return cols
    },
  },
  watch: {
    resetKey() {
      this.$refs.tablePageRef?.search?.()
    },
    isActive: {
      handler(v) {
        if (v) {
          this.$nextTick(() => {
            this.$refs.tablePageRef?.reload?.()
          })
        }
      },
      immediate: true,
    },
  },
  methods: {
    request(params) {
      return getPaymentMethodList(params)
    },
    buildParams({ query, page, limit }) {
      const params = {
        page,
        page_size: limit,
      }
      if (query?.username) params.username = query.username
      if (query?.card_no) params.card_no = query.card_no
      const uid = this.targetInfo?.uid
      if (uid) params.uid = uid
      return params
    },
    parseResult(response) {
      if (response.code !== 0) {
        this.$message.error(response.message || '加载失败')
        return { list: [], total: 0 }
      }
      return {
        list: response.data?.list || [],
        total: response.data?.total || 0,
      }
    },

    handleAdd() {
      this.isEdit = false
      this.formData = {
        id: '',
        card_type: '',
        pix_type: '',
        card_no: '',
        username: this.memberInfo?.username || this.memberInfo?.memberNo || '',
        extra1: '',
        real_name: '',
        mobile: '',
        email: '',
      }
      this.formDialogVisible = true
    },

    handleEdit(row) {
      this.isEdit = true
      this.formData = {
        id: row.id,
        card_type: row.card_type || '',
        pix_type: row.pix_type || '',
        card_no: row.card_no || '',
        username: row.username || '',
        extra1: row.extra1 || '',
        real_name: row.real_name || '',
        mobile: row.mobile || '',
        email: row.email || '',
      }
      this.formDialogVisible = true
    },

    async handleDelete(id) {
      this.$confirm('确定要删除该银行账户吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(async () => {
          try {
            const response = await deletePaymentMethod({ id })
            if (response.code === 0) {
              this.$message.success('删除成功')
              this.$refs.tablePageRef?.reload?.()
            } else {
              this.$message.error('删除失败')
            }
          } catch (error) {
            this.$message.error('删除失败')
          }
        })
        .catch(() => {
          this.$message.info('已取消删除')
        })
    },

    handleFormSuccess() {
      this.$refs.tablePageRef?.reload?.()
    },

    formatBankCardNo(cardNo) {
      if (!cardNo) return ''
      return `${cardNo.slice(0, 6)}${'*'.repeat(cardNo.length - 10)}${cardNo.slice(-4)}`
    },
  },
}
</script>

<style scoped>
.bank-card-cell {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  text-decoration: underline;
  cursor: pointer;
}
</style>
